import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ChevronRight, ChevronLeft, Check, Plus, Minus, AlertCircle, Lock } from 'lucide-react'
import * as Sentry from '@sentry/react'
import DashboardLayout from '../components/DashboardLayout'
import UpgradeCoachPlus from '../components/UpgradeCoachPlus'
import { useAuth } from '../context/AuthContext'
import { useFreeStatus } from '../hooks/useFreeStatus'
import { useCategoriesOptions } from '../constants/categories'
import matchService from '../services/matchService'
import { uploadFileMultipart, verifierFichierVideo } from '../services/multipartUpload'
import { hasAIPlanAccess } from '../utils/planAccess'
import { T } from '../theme'
import KitColorConfirm, { couleursCompletes } from '../components/KitColorConfirm'
import VideoDropSlots from '../components/VideoDropSlots'
import VideoScopeChoice, {
  reperesAttendus,
  SCOPE_FULL_MATCH, SCOPE_SINGLE_HALF, SCOPE_TWO_FILES,
  fichiersAttendus,
} from '../components/VideoScopeChoice'
import { useKitColorDefaults } from '../hooks/useKitColorDefaults'

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

const S = {
  label:  { display: 'block', fontFamily: T.mono, fontSize: 8, letterSpacing: '.2em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 },
  input:  { width: '100%', background: T.surface, border: `1px solid ${T.rule}`, padding: '12px 16px', color: T.ink, fontFamily: T.mono, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', background: T.surface, border: `1px solid ${T.rule}`, padding: '12px 16px', color: T.ink, fontFamily: T.mono, fontSize: 13, outline: 'none', cursor: 'pointer', boxSizing: 'border-box' },
  card:   { background: T.surface, border: `1px solid ${T.rule}`, padding: 28, borderTop: `2px solid ${T.gold}` },
  field:  { marginBottom: 20 },
  error:  { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 16, background: T.redBg, border: `1px solid ${T.redBdr}` },
}

const STEPS_ALL = [
  { num: 1, label: 'Infos match' },
  { num: 2, label: 'Score et events' },
  { num: 3, label: 'Ressenti' },
  { num: 4, label: 'Vidéo' },
  { num: 5, label: 'Analyse' },
]

const TIMELINE_MARKERS = [
  { key: 'kickOff1',  label: 'Coup d\'envoi MT1', color: '#4ade80' },
  { key: 'halfTime',  label: 'Fin de la MT1',     color: '#f97316' },
  { key: 'kickOff2',  label: 'Coup d\'envoi MT2', color: '#60a5fa' },
  { key: 'fullTime',  label: 'Fin du match',       color: '#f43f5e' },
]

// PARCOURS-VIDEO-DECLARE-001 · sur une video d'UNE SEULE periode, deux de ces
// quatre reperes N'EXISTENT PAS · il n'y a ni fin de MT1 ni coup d'envoi de
// MT2. Les exiger quand meme est ce qui laissait le bouton gris pour toujours.
//
// On garde les MEMES CLEFS (`kickOff1` / `fullTime`) plutot que d'en inventer
// deux · elles portent deja « debut » et « fin », et les reutiliser laisse
// `halfTime` et `kickOff2` a NULL. C'est ce qui protege le match de la reprise
// automatique · `recovery.py:142` exige les QUATRE reperes non nuls pour
// relancer une decoupe, il ignore donc nativement ce cas.
const MARQUEURS_UNE_PERIODE = [
  { key: 'kickOff1', label: 'Début de la période', color: '#4ade80' },
  { key: 'fullTime', label: 'Fin de la période',   color: '#f43f5e' },
]

// La DECISION vit dans `reperesAttendus` (VideoScopeChoice.jsx), partagee avec
// la page du match · deux lectures du meme fait divergeraient au premier
// correctif applique d'un seul cote. Ici on ne fait que choisir la liste qui
// correspond au nombre · 0 → aucune, 2 → debut/fin, 4 → les quatre.
function marqueursPour(scope) {
  const n = reperesAttendus(scope)
  if (n === 0) return []
  return n === 2 ? MARQUEURS_UNE_PERIODE : TIMELINE_MARKERS
}

// P0-WIZARD-SPLIT-001 · cadence du suivi de decoupe.
// Un tick qui echoue ne casse plus rien, on retente au suivant — d'ou le
// timeout par requete, plus court que l'intervalle : sans lui, une requete
// restee pendante suspend le suivi indefiniment. C'est le defaut qui a fige
// l'ecran du 09/08 alors que les deux mi-temps etaient deja en base.
const SPLIT_POLL_MS       = 4000
const SPLIT_REQ_TIMEOUT_MS = 15000
// Au-dela, on cesse de poller et on rend la main plutot que de laisser tourner
// un ecran d'attente que plus personne ne surveille. La decoupe, elle,
// continue cote serveur : le match reste accessible.
const SPLIT_MAX_WAIT_MS   = 45 * 60 * 1000

function splitLabel(status) {
  if (status === 'splitting')  return 'Découpe MT1 / MT2 en cours…'
  if (status === 'pending')    return 'Préparation de la découpe…'
  if (status === 'processing') return 'Traitement de la vidéo…'
  return 'Découpe MT1 / MT2 en cours…'
}

function fmtTime(secs) {
  if (secs == null || isNaN(secs)) return '--:--'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.floor(secs % 60)
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
}

const STEPS_FUTURE = [
  { num: 1, label: 'Infos match' },
]

const FEELINGS = [
  { value: 'satisfait', label: 'Satisfait',  emoji: '✓' },
  { value: 'mitige',    label: 'Mitigé',     emoji: '~' },
  { value: 'difficile', label: 'Difficile',  emoji: '✗' },
]

const EVENT_TYPES = [
  { value: 'goal',           label: 'But' },
  { value: 'assist',         label: 'Passe décisive' },
  { value: 'yellow',         label: 'Carton jaune' },
  { value: 'red',            label: 'Carton rouge' },
  { value: 'penalty_scored', label: 'Penalty marqué' },
  { value: 'own_goal',       label: 'CSC' },
]

function Field({ label, children }) {
  return <div style={S.field}><label style={S.label}>{label}</label>{children}</div>
}

function ErrorBanner({ message }) {
  if (!message) return null
  return (
    <div style={S.error}>
      <AlertCircle size={14} color={T.red} />
      <p style={{ fontFamily: T.mono, fontSize: 10, color: T.red, letterSpacing: '.06em', margin: 0 }}>{message}</p>
    </div>
  )
}

function StepIndicator({ current, steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {steps.map((s, i) => {
        const done   = current > s.num
        const active = current === s.num
        return (
          <div key={s.num} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? T.gold : active ? T.gold : 'transparent',
                border: `1px solid ${done || active ? T.gold : T.rule}`,
                fontFamily: T.mono, fontSize: 10, fontWeight: 700,
                color: done || active ? T.dark : T.muted,
              }}>
                {done ? <Check size={12} /> : s.num}
              </div>
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: active ? T.gold : T.muted, whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: current > s.num ? T.gold : T.rule, margin: '0 8px', marginBottom: 22 }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Modale upgrade plan FREE ───────────────────────────────────────
function UpgradeModal({ onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(10,9,8,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: T.surface, borderTop: `3px solid ${T.gold}`,
        maxWidth: 480, width: '100%', padding: '36px 32px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        {/* Icône */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, background: T.goldBg, border: `1px solid ${T.goldBdr}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={20} color={T.gold} />
          </div>
        </div>

        {/* Titre */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 10 }}>
            Plan Free
          </p>
          <h2 style={{ fontFamily: T.display, fontSize: 22, textTransform: 'uppercase', color: T.ink, lineHeight: 1.1, margin: '0 0 12px' }}>
            Tu as atteint<br/>tes 2 matchs d'essai.
          </h2>
          <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.75, letterSpacing: '.03em' }}>
            Le plan Free permet de découvrir Insightball avec 2 matchs. Tes matchs existants sont conservés. Pour continuer à saisir, passe au plan Coach+.
          </p>
        </div>

        {/* Séparateur */}
        <div style={{ height: 1, background: T.rule, margin: '24px 0' }} />

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {/* Coach+ */}
          <Link to="/dashboard/settings" onClick={onClose} style={{
            display: 'block', padding: '18px 20px',
            background: T.dark, border: `1.5px solid ${T.gold}`,
            textDecoration: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontFamily: T.display, fontSize: 16, fontWeight: 800, textTransform: 'uppercase', color: T.white || '#fff' }}>
                Coach<span style={{ color: T.gold }}>+</span>
              </span>
              <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: T.white || '#fff' }}>39€<span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>/mois</span></span>
            </div>
            <p style={{ fontFamily: T.mono, fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '.04em', margin: 0, lineHeight: 1.6 }}>
              Matchs illimités, studio vidéo, clips illimités, analyse automatique et statistiques collectives.
            </p>
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: T.goldBg, border: `1px solid ${T.goldBdr}`, padding: '3px 10px' }}>
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.gold }}>Essai 7 jours inclus</span>
            </div>
          </Link>
        </div>

        {/* Annuler */}
        <button onClick={onClose} style={{
          width: '100%', padding: '10px', background: 'transparent', border: 'none',
          fontFamily: T.mono, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase',
          color: T.muted, cursor: 'pointer',
        }}>
          Pas maintenant
        </button>
      </div>
    </div>
  )
}

export default function CreateMatch() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const { isExpired, formattedExpiresAt } = useFreeStatus()
  const { categories } = useCategoriesOptions()
  // Étape 5 — l'analyse est réservée aux plans IA. Un vrai FREE voit une carte
  // d'upgrade à la place du lancement.
  //
  // WIZARD-CLUB-001 · cette étape tenait sa PROPRE liste de plans en dur et
  // lisait `user.plan` brut. Un coach membre de club porte un plan personnel
  // FREE tout en étant couvert par l'abonnement de son club : il lisait
  // « LANCE L'ANALYSE AVEC COACH+ » et un bouton d'abonnement, alors que son
  // club paie déjà. Troisième occurrence du même défaut, après
  // hasAnalysesAccess et hasStudioAccess — d'où le détecteur partagé.
  const isAIPlan = hasAIPlanAccess(user)
  const paramDate = searchParams.get('date')
  const resumeId  = searchParams.get('resume')
  // P0-WIZARD-SPLIT-001 · `?split=1` signe une decoupe lancee. C'est le seul
  // etat du wizard qui survit a un rechargement, et il ne porte aucune donnee :
  // il dit seulement « va lire la base pour ce match ».
  const splitFlag = searchParams.get('split') === '1'

  // Free expire : la creation de match est verrouillee
  if (isExpired) {
    return (
      <DashboardLayout>
        <div style={{
          maxWidth: 540, margin: '60px auto', padding: 40,
          background: T.surface, border: `1px solid ${T.rule}`,
          borderTop: `2px solid ${T.gold}`, textAlign: 'center',
        }}>
          <Lock size={32} color={T.gold} style={{ margin: '0 auto 16px' }} />
          <h2 style={{
            fontFamily: T.display, fontSize: 22, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '.02em', margin: '0 0 12px',
          }}>
            Ton essai est termine.
          </h2>
          <p style={{
            fontFamily: T.mono, fontSize: 12, color: T.muted,
            lineHeight: 1.6, margin: '0 0 20px',
          }}>
            {formattedExpiresAt
              ? `Ton essai Free a pris fin le ${formattedExpiresAt}. `
              : 'Ton essai Free est termine. '}
            Passe en Coach+ pour creer de nouveaux matchs.
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            style={{
              padding: '12px 22px', background: T.gold, color: T.dark,
              border: 'none', fontFamily: T.mono, fontSize: 11, fontWeight: 700,
              letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            Passer en Coach+
          </button>
        </div>
      </DashboardLayout>
    )
  }

  const { kitHome, kitAway } = useKitColorDefaults()
  // Des que le coach touche a SA couleur, le pre-remplissage cesse : sa saisie
  // fait foi, y compris s'il change ensuite le lieu du match.
  const [kitTouche, setKitTouche] = useState(false)
  const [step, setStep] = useState(splitFlag ? 5 : resumeId ? 2 : 1)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Match créé en base après étape 1, ou injecté depuis ?resume=
  const [matchId, setMatchId] = useState(resumeId || null)

  // Étape 1 — infos match
  const [infos, setInfos] = useState({
    opponent:    '',
    // COULEURS-MAILLOT-001 · pre-remplies depuis le club par l'effet plus bas,
    // puis confirmees ou corrigees par le coach.
    kit_color_us:       '',
    kit_color_opponent: '',
    date:        paramDate || new Date().toISOString().split('T')[0],
    // BUG-012-bis : init vide, un useEffect ci-dessous applique
    // user.team_category comme smart default une fois la liste SSoT chargée.
    category:    '',
    type:        'championnat',
    competition: '',
    location:    '',
    is_home:     true,
    weather:     'Ensoleillé',
    pitch_type:  'Gazon naturel',
  })

  // Étape 2 — score + événements
  const [score, setScore] = useState({ home: '', away: '', home_ht: '', away_ht: '' })
  const [events, setEvents]   = useState([])
  const [players, setPlayers] = useState([])
  const [newEvent, setNewEvent] = useState({ player_id: '', event_type: 'goal', minute: '' })

  // Étape 3 — ressenti
  const [feeling, setFeeling]   = useState('')
  const [comment, setComment]   = useState('')

  // Étape 4 — vidéo optionnelle
  // PARCOURS-VIDEO-DECLARE-001 · `videoFiles` porte la selection (1 ou 2), et
  // `videoFile` reste le PREMIER · c'est lui qui alimente l'apercu et les
  // gardes existantes, qui n'ont pas a connaitre le second.
  const [videoFiles, setVideoFiles] = useState([])
  // Les emplacements sont CREUX · en mode deux fichiers, le coach peut n'avoir
  // rempli que la 2e periode. `videoFile` repond donc « y a-t-il quelque chose
  // a envoyer », pas « le premier fichier ».
  const videoFile = videoFiles.find(Boolean) || null
  const [videoScope, setVideoScope] = useState(SCOPE_FULL_MATCH)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Étape 5 — timeline + analyse
  const [videoUploaded, setVideoUploaded] = useState(false)
  const [videoKey, setVideoKey] = useState(null)
  const [analysesDisponibles, setAnalysesDisponibles] = useState(null)
  const [markers, setMarkers] = useState({ kickOff1: null, halfTime: null, kickOff2: null, fullTime: null })
  // P0-WIZARD-SPLIT-001 · l'etat d'orchestration vit en base, cet ecran le lit.
  // `splitPhase` ne dit pas ou en est la decoupe — seulement si cet ecran doit
  // interroger la base : null (wizard normal) | 'running' | 'failed'.
  const [splitPhase, setSplitPhase] = useState(splitFlag ? 'running' : null)
  const [splitStatusLabel, setSplitStatusLabel] = useState('')
  const videoRef5 = useRef(null)
  const [currentTime5, setCurrentTime5] = useState(0)
  const [duration5, setDuration5] = useState(0)
  const [localVideoUrl, setLocalVideoUrl] = useState(null)

  useEffect(() => {
    if (!videoFile) return
    const url = URL.createObjectURL(videoFile)
    setLocalVideoUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [videoFile])

  const fetchQuota = useCallback(async () => {
    if (analysesDisponibles !== null) return
    try {
      const res = await fetch(`${API}/matches/quota/`, { headers: authHeaders() })
      if (res.ok) {
        const d = await res.json()
        setAnalysesDisponibles(d.analyses_disponibles ?? 0)
      }
    } catch { setAnalysesDisponibles(0) }
  }, [analysesDisponibles])

  // Match futur (date > aujourd'hui) : l'étape Score est sautée
  const todayIso = new Date().toISOString().split('T')[0]
  const isFuture = infos.date > todayIso
  const activeSteps = isFuture ? STEPS_FUTURE : STEPS_ALL

  // Charger l'effectif dès le montage
  useEffect(() => {
    fetch(`${API}/players/`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setPlayers(Array.isArray(data) ? data : []))
      .catch(() => setPlayers([]))
  }, [])

  // Reprise wizard : charger les événements déjà enregistrés pour ce match
  useEffect(() => {
    if (!resumeId) return
    fetch(`${API}/matches/${resumeId}/events/`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [resumeId])

  // LA REPRISE RELIT LA DECLARATION — PARCOURS-VIDEO-DECLARE-002 (suite).
  //
  // `videoScope` partait de son defaut a chaque montage. Un coach qui reprenait
  // un match declare « deux fichiers » voyait donc « match entier » coche.
  //
  // CE N'EST PAS QU'UN AFFICHAGE FAUX · il clique sur ce qu'il voit. En
  // avancant, il REDECLARE — et cette fois par erreur. L'ecran lui fait ecraser
  // sa propre declaration, et le match repart en attente d'une decoupe que
  // personne n'attend. C'est exactement l'ecart entre ce qui est affiche et ce
  // qui est enregistre qu'on traque depuis deux jours.
  //
  // Une valeur hors contrat, ou son absence, retombe sur le defaut · NULL se
  // lit `full_match`, ici comme partout ailleurs.
  useEffect(() => {
    if (!resumeId) return
    let annule = false
    fetch(`${API}/matches/${resumeId}/`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(m => {
        if (annule || !m) return
        if ([SCOPE_FULL_MATCH, SCOPE_SINGLE_HALF, SCOPE_TWO_FILES].includes(m.video_scope)) {
          setVideoScope(m.video_scope)
        }
      })
      .catch(() => {})
    return () => { annule = true }
  }, [resumeId])

  // Smart default catégorie (BUG-012-bis) : pré-sélectionne user.team_category
  // dès que la liste SSoT est chargée, si elle y est. Sinon on laisse vide,
  // l'utilisateur choisit.
  useEffect(() => {
    if (infos.category) return
    if (!user?.team_category) return
    if (!categories.includes(user.team_category)) return
    setInfos(prev => ({ ...prev, category: user.team_category }))
  }, [categories, user, infos.category])

  // COULEURS-MAILLOT-001 · pre-remplissage du maillot du club.
  //
  // Depend de `is_home` : un club ne porte pas le meme jeu dehors et chez lui.
  // N'ECRASE JAMAIS une saisie du coach — la condition sur `kitTouche` — sinon
  // changer le lieu apres avoir choisi sa couleur la reecrirait en silence,
  // ce qui est exactement le genre d'erreur invisible que ce lot combat.
  useEffect(() => {
    if (kitTouche) return
    const defaut = infos.is_home ? kitHome : kitAway
    if (!defaut) return
    setInfos(prev => (prev.kit_color_us === defaut ? prev : { ...prev, kit_color_us: defaut }))
  }, [kitHome, kitAway, infos.is_home, kitTouche])

  // ── ÉTAPE 1 — Créer le match en DRAFT ────────────────────────
  async function handleStep1() {
    setError('')
    if (!infos.opponent.trim()) { setError("Le nom de l'adversaire est requis."); return }
    if (!infos.date) { setError("La date du match est requise."); return }
    // LES DEUX COULEURS SONT OBLIGATOIRES — PARCOURS-VIDEO-DECLARE-002.
    //
    // Elles etaient facultatives, avec la mention « l'analyse se fait quand
    // meme ». C'etait vrai, et c'etait le probleme · elle se faisait sans
    // savoir quelle equipe etait celle du coach.
    //
    // Deux messages DISTINCTS · « il en manque une » et « elles sont
    // identiques » ne se corrigent pas par le meme geste. Un message unique
    // obligerait le coach a deviner lequel des deux cas il est.
    if (infos.kit_color_us && infos.kit_color_us === infos.kit_color_opponent) {
      setError("Les deux équipes ne peuvent pas porter la même couleur de maillot.")
      return
    }
    if (!couleursCompletes(infos.kit_color_us, infos.kit_color_opponent)) {
      setError("Choisis la couleur de ton maillot et celle de l'adversaire.")
      return
    }


    setLoading(true)
    try {
      const res = await fetch(`${API}/matches/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          opponent:    infos.opponent.trim(),
          date:        new Date(infos.date).toISOString(),
          category:    infos.category,
          type:        infos.type,
          competition: infos.competition,
          location:    infos.location,
          is_home:     infos.is_home,
          weather:     infos.weather,
          pitch_type:  infos.pitch_type,
          // Chaine vide -> null · le backend refuse une valeur hors des 11
          // admises, et '' n'en fait pas partie.
          kit_color_us:       infos.kit_color_us || null,
          kit_color_opponent: infos.kit_color_opponent || null,
        }),
      })
      const data = await res.json()

      // ── Plan FREE : limite atteinte → modale upgrade ──
      if (res.status === 402) {
        const code = typeof data?.detail === 'object' ? data.detail?.code : null
        if (code === 'FREE_MATCH_LIMIT_REACHED') {
          setShowUpgradeModal(true)
          return
        }
        const msg = typeof data?.detail === 'object' ? data.detail?.message : (data?.detail || 'Limite atteinte.')
        setError(msg); return
      }

      if (!res.ok) {
        const msg = typeof data?.detail === 'string' ? data.detail : 'Erreur lors de la création du match.'
        setError(msg); return
      }
      setMatchId(data.id)
      if (isFuture) {
        navigate(`/dashboard/matches/${data.id}`)
      } else {
        setStep(2)
      }
    } catch {
      setError('Erreur réseau. Vérifie ta connexion.')
    } finally {
      setLoading(false)
    }
  }

  // ── ÉTAPE 2 — Score + événements ─────────────────────────────
  async function handleStep2() {
    setError('')
    if (score.home === '' || score.away === '') { setError('Le score est requis.'); return }

    setLoading(true)
    try {
      // Persister les événements locaux (ajoutés sans API call pendant l'étape 2)
      for (const ev of events) {
        if (ev._localId) {
          const res = await fetch(`${API}/matches/${matchId}/events/`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
              player_id:  ev.player_id,
              event_type: ev.event_type,
              minute:     ev.minute ?? null,
            }),
          })
          if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            const msg = typeof d?.detail === 'string' ? d.detail : 'Erreur lors de l\'enregistrement des événements.'
            setError(msg); return
          }
          const saved = await res.json()
          setEvents(prev => prev.map(e => e._localId === ev._localId ? saved : e))
        }
      }

      const patchRes = await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          score_home:    parseInt(score.home),
          score_away:    parseInt(score.away),
          score_home_ht: score.home_ht !== '' ? parseInt(score.home_ht) : null,
          score_away_ht: score.away_ht !== '' ? parseInt(score.away_ht) : null,
        }),
      })
      if (!patchRes.ok) { setError('Erreur lors de la sauvegarde du score.'); return }
      setStep(3)
    } catch {
      setError('Erreur réseau.')
    } finally {
      setLoading(false)
    }
  }

  function handleAddEvent() {
    setError('')
    if (!newEvent.player_id) { setError('Sélectionne un joueur.'); return }
    const player = players.find(p => p.id === newEvent.player_id)
    setEvents(prev => [...prev, {
      _localId:   crypto.randomUUID(),
      player_id:  newEvent.player_id,
      player_name: player?.name || '',
      event_type: newEvent.event_type,
      minute:     newEvent.minute !== '' ? parseInt(newEvent.minute) : null,
    }])
    setNewEvent({ player_id: '', event_type: 'goal', minute: '' })
  }

  async function handleRemoveEvent(ev) {
    if (ev.id) {
      try {
        await fetch(`${API}/matches/${matchId}/events/${ev.id}/`, {
          method: 'DELETE',
          headers: authHeaders(),
        })
      } catch { /* best effort */ }
    }
    setEvents(prev => prev.filter(e => (e.id || e._localId) !== (ev.id || ev._localId)))
  }

  // ── ÉTAPE 3 — Ressenti ────────────────────────────────────────
  async function handleStep3() {
    setError('')
    setLoading(true)
    try {
      const body = {}
      if (feeling) body.coach_feeling = feeling
      if (comment.trim()) body.coach_comment = comment.trim()

      if (Object.keys(body).length > 0) {
        const res = await fetch(`${API}/matches/${matchId}/`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(body),
        })
        if (!res.ok) { setError('Erreur lors de la sauvegarde du ressenti.'); return }
      }
      setStep(4)
    } catch {
      setError('Erreur réseau.')
    } finally {
      setLoading(false)
    }
  }

  // ── ÉTAPE 4 — Vidéo optionnelle ───────────────────────────────
  async function handleUploadVideo() {
    if (!videoFile || !matchId) return
    const deuxFichiers = videoScope === SCOPE_TWO_FILES
    setError('')
    setUploading(true)
    setUploadProgress(0)

    try {
      // ── 1+2. Upload multipart direct vers S3 ────────────────
      // MULTIPART-002 — remplace le PUT unique, qui plafonnait a 5 GiB
      // (plafond dur AWS) et bloquait tout match filme en entier. Le service
      // ouvre l'upload, signe et envoie les parties 4 par 4, finalise, et
      // abandonne proprement en cas d'echec pour ne pas laisser de fragments
      // factures. Il rend le file_key, qui sert a binder la video au match
      // juste apres.
      // ── DEUX FICHIERS · UN PAR UN, ET RATTACHES AU FUR ET A MESURE ──
      //
      // Chaque fichier est rattache DES QU'IL EST ARRIVE, par un appel a
      // `POST /matches/{id}/halves/`. Si le second echoue, le premier est deja
      // en base · le coach ne le renverra jamais. C'est exactement ce qui a
      // fait partir Hiblot · lui redemander plusieurs Go apres un echec.
      //
      // La declaration part AVANT les fichiers · le backend s'en sert pour
      // decider si le match attend une decoupe. Envoyee apres, elle laisserait
      // un `pending` que rien ne viendrait lever.
      if (deuxFichiers) {
        // Le scope part AVANT les fichiers · c'est lui qui dit au backend que
        // ce match n'attend aucune decoupe. Rattacher des mi-temps sur un match
        // encore lu comme `full_match` le laisserait en attente d'un traitement
        // qui n'aura jamais lieu.
        await fetch(`${API}/matches/${matchId}/`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ video_scope: videoScope }),
        })

        // L'INDEX PORTE LA PERIODE, PAS L'ORDRE D'ENVOI · l'emplacement 0 est
        // toujours MT1 et le 1 toujours MT2, meme si le coach n'a rempli que
        // le second. Se fier au rang dans la boucle rattacherait une 2e periode
        // comme MT1.
        const aEnvoyer = videoFiles
          .map((f, i) => (f ? { fichier: f, index: i } : null))
          .filter(Boolean)

        for (let rang = 0; rang < aEnvoyer.length; rang++) {
          const { fichier, index } = aEnvoyer[rang]
          const cle = await uploadFileMultipart(fichier, {
            // Une seule barre · chaque fichier occupe sa part de la course.
            onProgress: (p) => setUploadProgress(
              Math.round((rang * 100 + p) / aEnvoyer.length)
            ),
          })
          const res = await fetch(`${API}/matches/${matchId}/halves/`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ [index === 0 ? 'video_key_mt1' : 'video_key_mt2']: cle }),
          })
          if (!res.ok) {
            const corps = await res.json().catch(() => ({}))
            setError(corps?.detail || `Période ${index + 1} envoyée mais impossible de la lier au match.`)
            setUploading(false)
            return
          }
        }
        setVideoKey('halves')
        setVideoUploaded(true)
        await fetchQuota()
        setStep(5)
        return
      }

      const file_key = await uploadFileMultipart(videoFile, {
        onProgress: setUploadProgress,
      })

      // ── 3. PATCH match pour binder la video source (temporaire).
      // La declaration accompagne la video · c'est elle qui decide si le
      // backend pose `pending` (decoupe attendue) ou `ready` (rien a faire).
      const patchRes = await fetch(`${API}/matches/${matchId}/`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ video_url: file_key, video_scope: videoScope }),
      })
      if (!patchRes.ok) {
        const errBody = await patchRes.json().catch(() => ({}))
        setError(errBody?.detail || "Vidéo uploadée mais impossible de la lier au match.")
        setUploading(false)
        return
      }

      // ── 4. Passer à l'étape 5 (timeline / conversion) ───────
      setVideoKey(file_key)
      setVideoUploaded(true)
      await fetchQuota()
      setStep(5)
    } catch (err) {
      setError(err.message || "Erreur lors de l'upload.")
      // UPLOAD-ERR-001 — l'ecran ne montre au coach que la phrase utile. Le
      // detail technique part ici, seul endroit ou il reste exploitable une
      // fois le coach parti.
      //
      // Les refus attendus (quota FREE atteint, format ou taille hors bornes)
      // ne partent PAS en Sentry : ce sont des reponses du produit, pas des
      // pannes, et les remonter noierait les vrais refus S3.
      //
      // JAMAIS `videoFile.name` : un nom de fichier porte regulierement le nom
      // du club, de l'adversaire ou d'un joueur. L'extension seule repond a la
      // question posee (le suffixe entre dans la cle S3 sans normalisation).
      if (!err.metier) Sentry.captureException(err, {
        tags: { chantier: 'upload_s3_failed', s3_code: err.s3Code || 'inconnu' },
        extra: {
          http_status:     err.httpStatus,
          s3_code:         err.s3Code,
          s3_message:      err.s3Message,
          s3_request_id:   err.s3RequestId,
          file_size_bytes: videoFile?.size,
          file_type:       videoFile?.type,
          file_extension:  videoFile?.name?.split('.').pop(),
        },
      })
    } finally {
      setUploading(false)
    }
  }

  // PARCOURS-MITEMPS-UNIQUE-001 · ce bouton ne pose toujours AUCUN etat, et
  // c'est desormais correct · l'etat en base est deja juste (video presente,
  // `pending`, en attente de reperes). Ce qui manquait n'etait pas une
  // ecriture, c'etait que la destination le DISE.
  //
  // La page du match porte maintenant un bandeau « Ta vidéo est bien arrivée /
  // il reste une étape ». Le libelle du bouton, lui, mentait · « Passer cette
  // étape » annonce une etape facultative alors qu'aucune analyse n'est
  // possible sans elle. Il dit maintenant qu'on remet a plus tard, et ou l'on
  // va. C'est la trappe qui a fabrique les 9 matchs bloques mesures le 11/08.
  function handleFinishWithoutVideo() {
    navigate(`/dashboard/matches/${matchId}`)
  }

  const marqueursDemandes = marqueursPour(videoScope)
  const allMarkersSet = marqueursDemandes.every(m => markers[m.key] != null)
  // DEUX FICHIERS · aucun repere a poser. Voir `reperesAttendus` pour la
  // mesure · rien ne les lit dans ce mode, et l'ecran en demandait quatre sur
  // le PREMIER fichier seul, donc « coup d'envoi MT2 » sur une video qui
  // s'arrete a la pause.
  const sansMarqueurs = marqueursDemandes.length === 0

  // P0-WIZARD-SPLIT-001 · cette fonction ne fait plus qu'AMORCER la decoupe.
  // Elle n'attend plus la fin : le suivi est confie a l'effet ci-dessous, qui
  // lit la base. Auparavant toute la sequence (PATCH → split → poll → analyze)
  // tenait dans cette closure ; un seul maillon interrompu et l'ecran restait
  // fige sur « decoupe en cours » alors que les deux mi-temps etaient ecrites.
  async function handleValidateTimeline() {
    setError('')
    setSplitStatusLabel('Enregistrement des marqueurs…')
    setSplitPhase('running')
    try {
      await matchService.updateMatch(matchId, {
        // Aucun repere demande (deux fichiers) · les QUATRE partent a null,
        // et pas seulement « on ne les touche pas ». Un coach qui a repondu
        // « match entier », pose ses quatre reperes, puis s'est corrige en
        // « deux fichiers » laisserait sinon quatre valeurs perimees en base —
        // exactement ce que `recovery.py:142` prend pour une decoupe a relancer.
        timeline_kick_off_1: sansMarqueurs ? null : markers.kickOff1,
        // Sur une periode unique, ces deux reperes N'EXISTENT PAS · on envoie
        // explicitement `null` plutot que de laisser trainer une valeur d'un
        // choix precedent. Les quatre reperes non nuls sont ce qui declenche
        // la reprise automatique d'une decoupe (recovery.py:142) · en laisser
        // deux a NULL est ce qui protege ce match.
        timeline_half_time:  sansMarqueurs || videoScope === SCOPE_SINGLE_HALF ? null : markers.halfTime,
        timeline_kick_off_2: sansMarqueurs || videoScope === SCOPE_SINGLE_HALF ? null : markers.kickOff2,
        timeline_full_time:  sansMarqueurs ? null : markers.fullTime,
        video_scope:         videoScope,
      })

      // PARCOURS-VIDEO-DECLARE-001 · PAS DE DECOUPE HORS MATCH ENTIER.
      //
      // Sur une periode unique ou deux fichiers, la video est deja dans sa
      // forme finale · `split-halves` la refuserait (garde backend), et
      // `half_split.py` ECRASERAIT les mi-temps fournies s'il passait. Le
      // match est deja `ready`, on lance donc l'analyse directement.
      //
      // C'est aussi ce qui remplace l'enchainement automatique · sans decoupe,
      // `maybe_autostart_after_split` ne se declenche jamais.
      if (videoScope !== SCOPE_FULL_MATCH) {
        try { await matchService.launchAnalysis(matchId) } catch { /* bouton de la page du match */ }
        navigate(`/dashboard/matches/${matchId}`)
        return
      }

      await matchService.splitHalves(matchId)
      // Le match entre dans l'URL : c'est ce qui permet a un rechargement de
      // retrouver cet ecran au lieu de repartir a l'etape 1.
      setSearchParams({ resume: matchId, split: '1' }, { replace: true })
    } catch (err) {
      setError(err?.message || err?.response?.data?.detail || 'Erreur au lancement de la découpe.')
      setSplitPhase(null)
      setSplitStatusLabel('')
    }
  }

  // ── Suivi de la decoupe · la base fait foi ────────────────────
  //
  // Ancre au montage, pas a un clic : un rechargement pendant la decoupe
  // rebranche le suivi tout seul. Trois proprietes que la version precedente
  // n'avait pas :
  //   · un tick rate ne rompt pas le suivi, il est simplement retente ;
  //   · chaque requete a un timeout, aucune ne peut suspendre la boucle ;
  //   · le retour de l'onglet au premier plan relance un tick immediatement,
  //     sans attendre que le navigateur veuille bien redonner ses timers a un
  //     onglet qu'il avait mis en veille.
  //
  // Signal de fin : `has_halves`, c'est-a-dire video_url_mt1 ET video_url_mt2
  // en base. Le meme commit backend pose `status='ready'`, mais c'est la
  // presence des deux mi-temps qui dit que le travail est fait.
  useEffect(() => {
    if (splitPhase !== 'running' || !matchId) return

    let cancelled  = false
    let timer      = null
    let handedOff  = false
    const deadline = Date.now() + SPLIT_MAX_WAIT_MS

    const schedule = () => {
      if (cancelled || handedOff) return
      timer = setTimeout(tick, SPLIT_POLL_MS)
    }

    async function tick() {
      if (cancelled || handedOff) return

      if (Date.now() > deadline) {
        handedOff = true
        setSplitPhase('failed')
        setSplitStatusLabel('')
        setError("La découpe prend plus longtemps que prévu. Elle continue côté serveur : ouvre la page du match pour suivre la suite.")
        return
      }

      let s
      try {
        s = await matchService.getVideoStatus(matchId, { timeout: SPLIT_REQ_TIMEOUT_MS })
      } catch {
        // Reseau, 502, requete trop lente : sans consequence, on retente.
        schedule()
        return
      }
      if (cancelled || handedOff) return

      if (s.has_halves) {
        handedOff = true
        setSplitStatusLabel('Mi-temps prêtes · lancement de l\'analyse…')
        // L'analyse est deja enchainee cote serveur depuis le 02/08/2026 et la
        // route est idempotente. On l'appelle quand meme pour couvrir les cas
        // ou l'autostart a renonce, et un echec ici ne doit pas retenir le
        // coach sur cet ecran : la page du match porte le bouton manuel.
        try { await matchService.launchAnalysis(matchId) } catch { /* page du match */ }
        if (!cancelled) navigate(`/dashboard/matches/${matchId}`)
        return
      }

      if (s.status === 'failed') {
        handedOff = true
        setSplitPhase('failed')
        setSplitStatusLabel('')
        setError(s.error || 'La découpe MT1 / MT2 a échoué.')
        return
      }

      setSplitStatusLabel(splitLabel(s.status))
      schedule()
    }

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      clearTimeout(timer)
      tick()
    }

    tick()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [splitPhase, matchId, navigate])

  function adjustMarker(key, delta) {
    setMarkers(prev => {
      const cur = prev[key] ?? currentTime5
      return { ...prev, [key]: Math.max(0, Math.round(cur + delta)) }
    })
  }

  function poseMarker(key) {
    setMarkers(prev => ({ ...prev, [key]: Math.round(currentTime5) }))
  }

  const canGoNext = () => {
    // PARCOURS-VIDEO-DECLARE-002 · les couleurs entrent dans la condition du
    // bouton, PAS SEULEMENT dans le message d'erreur. Un bouton actif qui
    // refuse au clic fait deviner au coach ce qui manque ; un bouton eteint le
    // lui montre avant. Le message reste, il dit LEQUEL des deux cas.
    if (step === 1) return Boolean(
      infos.opponent.trim() && infos.date &&
      couleursCompletes(infos.kit_color_us, infos.kit_color_opponent)
    )
    if (step === 2) { const h = score.home !== '' ? parseInt(score.home) : null; const a = score.away !== '' ? parseInt(score.away) : null; return h !== null && !isNaN(h) && a !== null && !isNaN(a) }
    return true
  }

  function handleNext() {
    if (step === 1) handleStep1()
    else if (step === 2) handleStep2()
    else if (step === 3) handleStep3()
  }

  function handlePrev() {
    setError('')
    if (resumeId && step === 2) return
    setStep(s => s - 1)
  }

  return (
    <DashboardLayout>
      {/* Modale upgrade FREE */}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: T.gold, marginBottom: 8 }}>
            Nouveau match
          </p>
          <h1 style={{ fontFamily: T.display, fontSize: 22, color: T.ink, margin: 0 }}>
            {infos.opponent ? `vs ${infos.opponent}` : 'Suivi de match'}
          </h1>
        </div>

        <StepIndicator current={step} steps={activeSteps} />

        <ErrorBanner message={error} />

        {/* ── ÉTAPE 1 — Infos match ── */}
        {step === 1 && (
          <div style={S.card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Adversaire *">
                  <input
                    autoFocus
                    style={S.input}
                    value={infos.opponent}
                    onChange={e => setInfos(p => ({ ...p, opponent: e.target.value }))}
                    placeholder="Ex : AS Saint-Pierre"
                    onFocus={e => e.target.style.borderColor = T.gold}
                    onBlur={e => e.target.style.borderColor = T.rule}
                  />
                </Field>
              </div>

              <Field label="Date *">
                <input
                  type="date"
                  style={S.input}
                  value={infos.date}
                  onChange={e => setInfos(p => ({ ...p, date: e.target.value }))}
                />
              </Field>

              <Field label="Catégorie">
                <select style={S.select} value={infos.category} onChange={e => setInfos(p => ({ ...p, category: e.target.value }))} required>
                  <option value="" disabled>Sélectionner une catégorie</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <Field label="Type de match">
                <select style={S.select} value={infos.type} onChange={e => setInfos(p => ({ ...p, type: e.target.value }))}>
                  <option value="championnat">Championnat</option>
                  <option value="coupe">Coupe</option>
                  <option value="amical">Amical</option>
                </select>
              </Field>

              <Field label="Compétition">
                <input
                  style={S.input}
                  value={infos.competition}
                  onChange={e => setInfos(p => ({ ...p, competition: e.target.value }))}
                  placeholder="Ex : National 3 — Poule C"
                  onFocus={e => e.target.style.borderColor = T.gold}
                  onBlur={e => e.target.style.borderColor = T.rule}
                />
              </Field>

              <Field label="Lieu">
                <select style={S.select} value={infos.is_home ? 'home' : 'away'} onChange={e => setInfos(p => ({ ...p, is_home: e.target.value === 'home' }))}>
                  <option value="home">Domicile</option>
                  <option value="away">Extérieur</option>
                </select>
              </Field>

              <Field label="Météo">
                <select style={S.select} value={infos.weather} onChange={e => setInfos(p => ({ ...p, weather: e.target.value }))}>
                  {['Ensoleillé', 'Nuageux', 'Pluvieux', 'Venteux', 'Neige'].map(w => <option key={w}>{w}</option>)}
                </select>
              </Field>

              <Field label="Terrain">
                <select style={S.select} value={infos.pitch_type} onChange={e => setInfos(p => ({ ...p, pitch_type: e.target.value }))}>
                  <option>Gazon naturel</option>
                  <option>Gazon synthétique</option>
                </select>
              </Field>
            </div>

            {/* COULEURS-MAILLOT-001 · ICI et pas a l'ecran d'analyse.
                L'analyse demarre TOUTE SEULE a la fin de la decoupe
                (`maybe_autostart_after_split` cote serveur) : il n'y a pas de
                clic « lancer » a attendre. Les couleurs doivent etre en base
                avant, donc au moment ou le match se cree. */}
            <KitColorConfirm
              couleurNous={infos.kit_color_us}
              couleurAdverse={infos.kit_color_opponent}
              onChangeNous={v => { setKitTouche(true); setInfos(p => ({ ...p, kit_color_us: v })) }}
              onChangeAdverse={v => setInfos(p => ({ ...p, kit_color_opponent: v }))}
            />
          </div>
        )}

        {/* ── ÉTAPE 2 — Score + événements ── */}
        {step === 2 && (
          <div style={S.card}>
            <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 20 }}>
              Score
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'end', marginBottom: 24 }}>
              <div>
                <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.gold, marginBottom: 6, textAlign: 'center' }}>{infos.is_home ? (user?.club_name || 'Nous') : infos.opponent}</div>
                <input type="number" min="0" max="30" style={{ ...S.input, textAlign: 'center', fontSize: 28, fontWeight: 700, padding: '16px 8px' }}
                  value={score.home} onChange={e => setScore(p => ({ ...p, home: e.target.value }))}
                  placeholder="0"
                  onFocus={e => e.target.style.borderColor = T.gold} onBlur={e => e.target.style.borderColor = T.rule}
                />
              </div>
              <div style={{ fontFamily: T.mono, fontSize: 22, color: T.muted, paddingBottom: 14, textAlign: 'center' }}>—</div>
              <div>
                <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.gold, marginBottom: 6, textAlign: 'center' }}>{infos.is_home ? infos.opponent : (user?.club_name || 'Nous')}</div>
                <input type="number" min="0" max="30" style={{ ...S.input, textAlign: 'center', fontSize: 28, fontWeight: 700, padding: '16px 8px' }}
                  value={score.away} onChange={e => setScore(p => ({ ...p, away: e.target.value }))}
                  placeholder="0"
                  onFocus={e => e.target.style.borderColor = T.gold} onBlur={e => e.target.style.borderColor = T.rule}
                />
              </div>
            </div>

            <details style={{ marginBottom: 28 }}>
              <summary style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, cursor: 'pointer', userSelect: 'none', marginBottom: 12 }}>
                Score mi-temps (optionnel)
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', marginTop: 12 }}>
                <input type="number" min="0" max="30" style={{ ...S.input, textAlign: 'center' }}
                  value={score.home_ht} onChange={e => setScore(p => ({ ...p, home_ht: e.target.value }))} placeholder="—"
                />
                <span style={{ fontFamily: T.mono, color: T.muted, textAlign: 'center' }}>—</span>
                <input type="number" min="0" max="30" style={{ ...S.input, textAlign: 'center' }}
                  value={score.away_ht} onChange={e => setScore(p => ({ ...p, away_ht: e.target.value }))} placeholder="—"
                />
              </div>
            </details>

            <div style={{ borderTop: `1px solid ${T.rule}`, paddingTop: 24 }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 16 }}>
                Événements
              </div>

              {events.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {events.map(ev => (
                    <div key={ev.id || ev._localId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: T.bgAlt, marginBottom: 6, border: `1px solid ${T.rule}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontFamily: T.mono, fontSize: 9, color: T.gold, letterSpacing: '.08em' }}>
                          {EVENT_TYPES.find(t => t.value === ev.event_type)?.label || ev.event_type}
                        </span>
                        <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink }}>{ev.player_name}</span>
                        {ev.minute && <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted }}>{ev.minute}'</span>}
                      </div>
                      <button onClick={() => handleRemoveEvent(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, padding: 4, display: 'flex' }}>
                        <Minus size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {isMobile ? (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label style={S.label}>Joueur</label>
                    <select style={S.select} value={newEvent.player_id} onChange={e => setNewEvent(p => ({ ...p, player_id: e.target.value }))}>
                      <option value="">Sélectionner...</option>
                      {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px auto', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={S.label}>Type</label>
                      <select style={S.select} value={newEvent.event_type} onChange={e => setNewEvent(p => ({ ...p, event_type: e.target.value }))}>
                        {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Min.</label>
                      <input type="number" min="0" max="130" style={S.input} placeholder="—"
                        value={newEvent.minute} onChange={e => setNewEvent(p => ({ ...p, minute: e.target.value }))}
                      />
                    </div>
                    <button onClick={handleAddEvent} style={{ padding: '12px 14px', background: T.gold, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Plus size={16} color={T.dark} />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 60px auto', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label style={S.label}>Joueur</label>
                    <select style={S.select} value={newEvent.player_id} onChange={e => setNewEvent(p => ({ ...p, player_id: e.target.value }))}>
                      <option value="">Sélectionner...</option>
                      {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Type</label>
                    <select style={S.select} value={newEvent.event_type} onChange={e => setNewEvent(p => ({ ...p, event_type: e.target.value }))}>
                      {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Min.</label>
                    <input type="number" min="0" max="130" style={S.input} placeholder="—"
                      value={newEvent.minute} onChange={e => setNewEvent(p => ({ ...p, minute: e.target.value }))}
                    />
                  </div>
                  <button onClick={handleAddEvent} style={{ padding: '12px 14px', background: T.gold, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plus size={16} color={T.dark} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 — Ressenti coach ── */}
        {step === 3 && (
          <div style={S.card}>
            <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 24 }}>
              Ton ressenti sur le match
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 28 }}>
              {FEELINGS.map(f => (
                <button key={f.value} onClick={() => setFeeling(f.value)} style={{
                  padding: '20px 12px', border: `1px solid ${feeling === f.value ? T.gold : T.rule}`,
                  background: feeling === f.value ? T.goldBg : T.surface,
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'all .15s',
                }}>
                  <span style={{ fontFamily: T.mono, fontSize: 18, color: feeling === f.value ? T.gold : T.muted }}>{f.emoji}</span>
                  <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: feeling === f.value ? T.gold : T.muted }}>
                    {f.label}
                  </span>
                </button>
              ))}
            </div>

            <Field label="Commentaire (optionnel)">
              <textarea
                rows={4}
                style={{ ...S.input, resize: 'vertical', lineHeight: 1.6 }}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Notes sur le match, axes à travailler, état du groupe..."
                onFocus={e => e.target.style.borderColor = T.gold}
                onBlur={e => e.target.style.borderColor = T.rule}
              />
            </Field>

            <p style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.06em', marginTop: 8 }}>
              Ces notes sont privées. Elles seront croisées avec tes statistiques sur la durée.
            </p>
          </div>
        )}

        {/* ── ÉTAPE 4 — Vidéo ── */}
        {step === 4 && (
          <div style={S.card}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 6 }}>
                Studio vidéo
              </div>
              <h2 style={{ fontFamily: T.display, fontSize: 22, textTransform: 'uppercase', color: T.ink, lineHeight: 1.1, margin: '0 0 8px' }}>
                Charge ta vidéo de match
              </h2>
              <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink2, lineHeight: 1.6, margin: '0 0 6px', letterSpacing: '.02em' }}>
                Tu pourras utiliser le Studio pour marquer tes moments clés, tagger tes joueurs et extraire des clips.
              </p>
              <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5, margin: 0, letterSpacing: '.02em' }}>
                MP4 ou MOV, jusqu'à 8 GB. Tu peux passer cette étape, le match reste enregistré sans vidéo.
              </p>
            </div>

            <VideoScopeChoice valeur={videoScope} onChange={(v) => {
              setVideoScope(v)
              // LA DECLARATION EST PERSISTEE DES QU'ELLE EST FAITE.
              //
              // Elle ne partait qu'AVEC la video · un coach qui declarait puis
              // s'en allait laissait `video_scope` a NULL, et la page du match
              // lui reproposait le parcours generique. Releve en production sur
              // 172ff259 le 11/08 · le coach avait bien choisi « deux fichiers »
              // et la base n'en gardait aucune trace.
              //
              // Sans attendre la reponse · c'est une preference, pas une
              // donnee critique. Un echec reseau ne doit pas empecher le coach
              // de choisir son fichier, et l'envoi de la video la reposera.
              if (matchId) {
                fetch(`${API}/matches/${matchId}/`, {
                  method: 'PATCH',
                  headers: authHeaders(),
                  body: JSON.stringify({ video_scope: v }),
                }).catch(() => {})
              }
              // Changer d'avis ne doit RIEN couter · mais une selection de deux
              // fichiers devenue invalide (passage a « un fichier ») doit etre
              // relachee, sinon on enverrait un fichier que le coach ne voit
              // plus dans la liste.
              setVideoFiles(prev => prev.slice(0, fichiersAttendus(v)))
            }} />

            {/* PARCOURS-VIDEO-DECLARE-002 · UN EMPLACEMENT PAR FICHIER
                ATTENDU. La selection groupee ne tenait pas · les deux fichiers
                ne sont pas toujours dans le meme dossier, et le second est
                souvent encore en transfert depuis le telephone. Le coach peut
                n'en remplir qu'un, envoyer, et revenir plus tard pour l'autre. */}
            {!uploading && (
              <VideoDropSlots
                scope={videoScope}
                fichiers={videoFiles}
                onChoisir={(i, f) => {
                  // Refus a la selection, pas apres le transfert · un .mkv ou
                  // un fichier de 9 Go part sinon pour plusieurs minutes avant
                  // d'etre refuse. Le coach doit savoir avant d'attendre.
                  const invalide = verifierFichierVideo(f)
                  if (invalide) { setError(invalide); return }
                  setError('')
                  setVideoFiles(prev => {
                    const suivant = [...prev]
                    suivant[i] = f
                    return suivant
                  })
                }}
                onRetirer={(i) => setVideoFiles(prev => {
                  const suivant = [...prev]
                  suivant[i] = null
                  return suivant
                })}
              />
            )}

            {uploading && (
              <div style={{ padding: '20px 18px', background: T.surface, border: `1px solid ${T.goldBdr}`, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.gold }}>
                    {uploadProgress < 100 ? 'Upload en cours' : 'Finalisation'}
                  </span>
                  <span style={{ fontFamily: T.display, fontSize: 14, color: T.gold, lineHeight: 1 }}>
                    {uploadProgress}%
                  </span>
                </div>
                <div style={{ height: 4, width: '100%', background: 'rgba(79,142,247,0.12)' }}>
                  <div style={{
                    height: '100%',
                    width: `${uploadProgress}%`,
                    background: T.gold,
                    transition: 'width .3s ease',
                  }} />
                </div>
                <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5, margin: '12px 0 0', letterSpacing: '.02em' }}>
                  Ne ferme pas cet onglet pendant l'upload. Le traitement vidéo (optimisation faststart) continue côté serveur une fois l'upload terminé. Tu pourras lancer l'analyse depuis la page du match quand la vidéo sera prête.
                </p>
              </div>
            )}

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={handleUploadVideo}
                disabled={!videoFile || uploading}
                style={{
                  flex: 1, minWidth: 200, padding: '14px 24px',
                  background: (!videoFile || uploading) ? 'rgba(79,142,247,0.2)' : T.gold,
                  color: (!videoFile || uploading) ? T.gold : T.dark,
                  fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                  border: (!videoFile || uploading) ? `1px solid ${T.goldBdr}` : 'none',
                  cursor: (!videoFile || uploading) ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? 'Upload en cours…' : 'Uploader la vidéo →'}
              </button>
              <button
                onClick={handleFinishWithoutVideo}
                disabled={uploading}
                style={{
                  padding: '14px 22px',
                  background: 'transparent', border: `1px solid ${T.rule}`,
                  color: T.muted,
                  fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                }}
              >
                Skipper la vidéo
              </button>
            </div>

          </div>
        )}

        {/* ── ÉTAPE 5 — Découpe en cours · l'écran suit l'état en base ── */}
        {step === 5 && splitPhase && (
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid ${splitPhase === 'failed' ? T.red : T.gold}`, padding: 32, textAlign: 'center' }}>
            {splitPhase === 'running' ? (
              <>
                <div style={{ width: 34, height: 34, margin: '0 auto 20px', border: `2px solid ${T.goldBdr}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 12 }}>
                  Traitement vidéo
                </div>
                <h2 style={{ fontFamily: T.display, fontSize: 20, textTransform: 'uppercase', color: T.ink, lineHeight: 1.15, margin: '0 0 14px' }}>
                  {splitStatusLabel || 'Découpe MT1 / MT2 en cours…'}
                </h2>
                <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.7, letterSpacing: '.02em', margin: '0 0 22px' }}>
                  Compte quelques minutes. Le traitement tourne sur nos serveurs : tu peux fermer cette page, il continue sans toi et l'analyse s'enchaîne toute seule.
                </p>
                <button
                  onClick={handleFinishWithoutVideo}
                  style={{ padding: '11px 22px', background: 'transparent', border: `1px solid ${T.rule}`, color: T.muted, fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                >
                  Voir la page du match →
                </button>
              </>
            ) : (
              <>
                <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.red, marginBottom: 12 }}>
                  Traitement vidéo
                </div>
                <h2 style={{ fontFamily: T.display, fontSize: 20, textTransform: 'uppercase', color: T.ink, lineHeight: 1.15, margin: '0 0 14px' }}>
                  La découpe n'a pas abouti
                </h2>
                <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.7, letterSpacing: '.02em', margin: '0 0 22px' }}>
                  Ta vidéo et tes marqueurs sont enregistrés. Tu peux relancer le traitement depuis la page du match.
                </p>
                <button
                  onClick={handleFinishWithoutVideo}
                  style={{ padding: '12px 24px', background: T.gold, color: T.dark, fontFamily: T.mono, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}
                >
                  Voir le match →
                </button>
              </>
            )}
          </div>
        )}

        {/* ── ÉTAPE 5 — Analyse (timeline ou conversion Free) ── */}
        {step === 5 && !splitPhase && !isAIPlan && (
          <div style={{ background: T.dark, border: `2px solid ${T.gold}`, padding: 32 }}>
            <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 16 }}>
              Ta vidéo est prête
            </div>
            <h2 style={{ fontFamily: T.display, fontSize: 24, textTransform: 'uppercase', color: T.cream || '#f5f2eb', lineHeight: 1.05, margin: '0 0 18px' }}>
              LANCE L'ANALYSE<br />AVEC COACH+
            </h2>
            <p style={{ fontFamily: T.body, fontSize: 14, color: 'rgba(245,242,235,0.7)', lineHeight: 1.7, margin: '0 0 28px' }}>
              L'analyse génère automatiquement les statistiques collectives de ton match à partir de ta vidéo.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => navigate('/dashboard/settings')}
                style={{ padding: '15px 24px', background: T.gold, color: T.dark, fontFamily: T.mono, fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', width: '100%' }}
              >
                PASSER EN COACH+ →
              </button>
              <button
                onClick={handleFinishWithoutVideo}
                style={{ padding: '14px 22px', background: 'transparent', border: '1px solid rgba(245,242,235,0.15)', color: 'rgba(245,242,235,0.4)', fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', width: '100%' }}
              >
                Continuer sans analyse
              </button>
            </div>
          </div>
        )}

        {step === 5 && !splitPhase && isAIPlan && analysesDisponibles === 0 && (
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid ${T.goldBdr}`, padding: 28, textAlign: 'center' }}>
            <div style={{ fontFamily: T.display, fontSize: 18, textTransform: 'uppercase', color: T.ink, marginBottom: 10 }}>Plus d'analyses disponibles</div>
            <div style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.7, marginBottom: 20 }}>
              Ta vidéo est bien enregistrée. Tu pourras lancer l'analyse depuis la page du match une fois rechargé.
            </div>
            <button
              onClick={handleFinishWithoutVideo}
              style={{ padding: '12px 24px', background: T.gold, color: T.dark, fontFamily: T.mono, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer' }}
            >
              Voir le match →
            </button>
          </div>
        )}

        {step === 5 && !splitPhase && isAIPlan && analysesDisponibles > 0 && (
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.rule}` }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, marginBottom: 6 }}>
                {sansMarqueurs ? 'Prêt à analyser' : 'Marqueurs timeline'}
              </div>
              <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.6, margin: 0, letterSpacing: '.02em' }}>
                {sansMarqueurs
                  ? "Tes deux fichiers sont enregistrés, une période chacun. Il n'y a aucun repère à poser : chaque fichier est déjà une période complète."
                  : `Navigue dans la vidéo et pose les ${marqueursDemandes.length} marqueurs. Les zones hors-jeu seront ignorées par l'analyse.`}
              </p>
            </div>

            {/* Player vidéo · MASQUE quand aucun repere n'est demande. Il ne
                montre que `videoFiles.find(Boolean)`, donc le PREMIER fichier ·
                l'afficher a cote d'une periode qu'il ne contient pas est ce
                qui rendait l'ecran incomprehensible en deux fichiers. */}
            {localVideoUrl && !sansMarqueurs && (
              <div style={{ background: '#000', position: 'relative' }}>
                <video
                  ref={videoRef5}
                  src={localVideoUrl}
                  style={{ width: '100%', maxHeight: 260, display: 'block' }}
                  onTimeUpdate={e => setCurrentTime5(e.target.currentTime)}
                  onLoadedMetadata={e => setDuration5(e.target.duration)}
                  controls
                />
              </div>
            )}

            {/* Marqueurs */}
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {marqueursDemandes.map(m => {
                const val = markers[m.key]
                return (
                  <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: val != null ? `rgba(${m.color === '#4ade80' ? '74,222,128' : m.color === '#f97316' ? '249,115,22' : m.color === '#60a5fa' ? '96,165,250' : '244,63,94'},.06)` : T.bg, border: `1px solid ${val != null ? m.color + '55' : T.rule}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: val != null ? m.color : T.muted, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: val != null ? T.ink : T.muted }}>{m.label}</div>
                      <div style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 700, color: val != null ? T.ink : T.muted, marginTop: 2 }}>{val != null ? fmtTime(val) : '—'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {[{ d: -10, l: '−10s' }, { d: -1, l: '−1s' }, { d: 1, l: '+1s' }, { d: 10, l: '+10s' }].map(({ d, l }) => (
                        <button key={d} onClick={() => adjustMarker(m.key, d)} disabled={val == null}
                          style={{ padding: '4px 8px', fontFamily: T.mono, fontSize: 9, background: 'transparent', border: `1px solid ${T.rule}`, color: val != null ? T.muted : T.rule, cursor: val != null ? 'pointer' : 'not-allowed', letterSpacing: '.04em' }}>
                          {l}
                        </button>
                      ))}
                      <button onClick={() => poseMarker(m.key)}
                        style={{ padding: '6px 14px', fontFamily: T.mono, fontSize: 9, fontWeight: 700, background: T.gold, color: T.dark, border: 'none', cursor: 'pointer', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                        Poser
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* CTAs */}
            <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {/* Le suivi de decoupe a son propre ecran : ce bloc disparait
                  des que la decoupe est lancee, le bouton n'a plus a porter
                  l'etat du traitement. */}
              <button
                onClick={handleValidateTimeline}
                disabled={!allMarkersSet}
                style={{
                  flex: 1, minWidth: 200, padding: '14px 24px',
                  background: allMarkersSet ? T.gold : 'rgba(79,142,247,0.2)',
                  color: allMarkersSet ? T.dark : T.gold,
                  fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                  border: allMarkersSet ? 'none' : `1px solid ${T.goldBdr}`,
                  cursor: allMarkersSet ? 'pointer' : 'not-allowed',
                }}
              >
                {sansMarqueurs ? "Lancer l'analyse →" : "Valider et lancer l'analyse →"}
              </button>
              <button
                onClick={handleFinishWithoutVideo}
                style={{ padding: '14px 22px', background: 'transparent', border: `1px solid ${T.rule}`, color: T.muted, fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}
                title={sansMarqueurs
                  ? 'Tes fichiers restent enregistrés. Tu pourras lancer l\'analyse depuis la page du match.'
                  : 'La vidéo reste enregistrée. Tu pourras placer les repères depuis la page du match.'}
              >
                Finir plus tard
              </button>
            </div>
          </div>
        )}

        {/* ── Navigation ── */}
        {/* Masquee pendant le suivi de decoupe : « Précédent » y ramenerait a
            l'etape vidéo d'un match dont la vidéo est deja partie au découpage. */}
        <div style={{ display: splitPhase ? 'none' : 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          {(resumeId && step === 2) ? (
            <button onClick={() => navigate(`/dashboard/matches/${matchId}`)} style={{ padding: '12px 22px', background: 'transparent', border: 'none', color: T.muted, cursor: 'pointer', fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              Annuler
            </button>
          ) : step > 1 ? (
            <button onClick={handlePrev} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'transparent', border: `1px solid ${T.rule}`, color: T.muted, cursor: 'pointer', fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              <ChevronLeft size={16} /> Précédent
            </button>
          ) : (
            <button onClick={() => navigate('/dashboard/matches')} style={{ padding: '12px 22px', background: 'transparent', border: 'none', color: T.muted, cursor: 'pointer', fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              Annuler
            </button>
          )}

          {step <= 3 && (
            <button
              onClick={handleNext}
              disabled={loading || !canGoNext()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
                background: canGoNext() ? T.gold : 'rgba(79,142,247,0.2)',
                color: canGoNext() ? T.dark : T.gold,
                fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                border: canGoNext() ? 'none' : `1px solid ${T.goldBdr}`,
                cursor: loading || !canGoNext() ? 'not-allowed' : 'pointer',
              }}
            >
              {loading
                ? <div style={{ width: 14, height: 14, border: `2px solid rgba(15,15,13,0.3)`, borderTopColor: T.dark, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
                : <><span>{isFuture ? 'Créer le match' : 'Suivant'}</span><ChevronRight size={16} /></>
              }
            </button>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
